import { SignIn } from '@clerk/clerk-react';
import type { ComponentProps } from 'react';

type ClerkAppearance = NonNullable<ComponentProps<typeof SignIn>['appearance']>;

/**
 * Clerk appearance mapped to Circus Daily Pick’em tokens (Figma CD — Pick-em, Sign In).
 * Uses CSS variables from `index.css` so tenant/theme changes stay in sync.
 */
export const clerkAppearance: ClerkAppearance = {
  variables: {
    colorPrimary: 'var(--color-brand)',
    colorDanger: 'var(--error-200)',
    colorSuccess: 'var(--color-accent-green)',
    colorWarning: 'var(--color-accent-gold)',
    colorNeutral: 'var(--color-white)',
    colorBackground: 'var(--color-charcoal)',
    colorInputBackground: 'var(--color-surface)',
    colorInputText: 'var(--color-text)',
    colorText: 'var(--color-text)',
    colorTextSecondary: 'rgba(255, 255, 255, 0.4)',
    colorTextOnPrimaryBackground: 'var(--color-white)',
    borderRadius: '0.5rem',
    fontFamily: 'Poppins, ui-sans-serif, system-ui, sans-serif',
    fontFamilyButtons: 'Poppins, ui-sans-serif, system-ui, sans-serif',
    fontSize: '0.875rem',
  },
  elements: {
    rootBox: {
      width: '100%',
      maxWidth: '100%',
      overflow: 'visible',
    },
    cardBox: {
      boxShadow: 'none',
      width: '100%',
      maxWidth: '100%',
      overflow: 'visible',
    },
    card: {
      background: 'transparent',
      boxShadow: 'none',
      border: 'none',
      paddingTop: '1.5rem',
      paddingBottom: '1.5rem',
      paddingLeft: '0',
      paddingRight: '0',
      width: '100%',
      maxWidth: '100%',
      gap: '1.5rem',
      overflow: 'visible',
      boxSizing: 'border-box',
    },
    scrollBox: {
      overflow: 'visible',
      paddingLeft: '0',
      paddingRight: '0',
    },
    main: {
      gap: '1.5rem',
      overflow: 'visible',
      width: '100%',
      boxSizing: 'border-box',
      paddingLeft: '0',
      paddingRight: '0',
    },
    header: {
      textAlign: 'center',
      marginBottom: '0',
    },
    headerTitle: {
      fontFamily: 'Poppins, ui-sans-serif, system-ui, sans-serif',
      fontWeight: '800',
      fontSize: '2rem',
      lineHeight: '2rem',
      color: 'var(--color-text)',
      textAlign: 'center',
    },
    headerSubtitle: {
      display: 'none',
    },
    logoBox: {
      display: 'none',
    },
    form: {
      // Figma Form Slot gap = padding/xl (24px)
      gap: '1.5rem',
    },
    formFieldRow: {
      marginBottom: '0',
    },
    formField: {
      marginBottom: '0',
    },
    formFieldLabelRow: {
      marginBottom: '0.5rem',
    },
    formFieldLabel: {
      fontFamily: 'Poppins, ui-sans-serif, system-ui, sans-serif',
      fontWeight: '400',
      fontSize: '0.75rem',
      lineHeight: '0.75rem',
      color: 'var(--color-text)',
      textTransform: 'capitalize',
      marginBottom: '0',
    },
    formFieldInput: {
      backgroundColor: 'var(--color-surface)',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'transparent',
      borderRadius: '0.5rem',
      height: '3rem',
      minHeight: '3rem',
      maxHeight: '3rem',
      padding: '1rem',
      fontSize: '0.875rem',
      lineHeight: '1.25rem',
      color: 'var(--color-text)',
      // Figma active highlight is a white border — kill Clerk's primary-colored ring
      boxShadow: 'none',
      outline: 'none',
      boxSizing: 'border-box',
      transitionProperty: 'border-color',
      transitionDuration: '150ms',
      '&:hover': {
        backgroundColor: 'var(--color-surface)',
        borderColor: 'transparent',
        boxShadow: 'none',
      },
      '&:focus': {
        backgroundColor: 'var(--color-surface)',
        borderColor: 'var(--color-white)',
        boxShadow: 'none',
        outline: 'none',
      },
      '&:focus-visible': {
        backgroundColor: 'var(--color-surface)',
        borderColor: 'var(--color-white)',
        boxShadow: 'none',
        outline: 'none',
      },
      '&[data-focus="true"], &[aria-invalid="false"]:focus': {
        borderColor: 'var(--color-white)',
        boxShadow: 'none',
      },
    },
    formFieldInputShowPasswordButton: {
      width: '1.25rem',
      height: '1.25rem',
      minWidth: '1.25rem',
      minHeight: '1.25rem',
      padding: '0',
      color: 'var(--color-white)',
      background: 'transparent',
      border: 'none',
      boxShadow: 'none',
    },
    input: {
      color: 'var(--color-text)',
    },
    formFieldInputGroup: {
      borderRadius: '0.5rem',
      boxShadow: 'none',
      backgroundColor: 'transparent',
    },
    checkbox: {
      width: '1.125rem',
      height: '1.125rem',
      borderRadius: '0.25rem',
      border: '1px solid var(--color-white)',
      backgroundColor: 'transparent',
      accentColor: 'var(--color-brand)',
    },
    checkboxLabel: {
      fontFamily: 'Poppins, ui-sans-serif, system-ui, sans-serif',
      fontWeight: '400',
      fontSize: '0.875rem',
      lineHeight: '1.25rem',
      color: 'var(--color-text)',
    },
    formFieldAction: {
      fontFamily: 'Poppins, ui-sans-serif, system-ui, sans-serif',
      fontWeight: '600',
      fontSize: '0.875rem',
      lineHeight: '1rem',
      color: 'var(--color-text)',
      textDecoration: 'underline',
      textUnderlineOffset: '2px',
    },
    formButtonPrimary: {
      backgroundColor: 'var(--color-brand)',
      border: '2px solid var(--color-brand)',
      borderRadius: '999px',
      minHeight: '3rem',
      height: '3rem',
      padding: '0.75rem 1.5rem',
      fontFamily: 'Poppins, ui-sans-serif, system-ui, sans-serif',
      fontWeight: '600',
      fontSize: '1rem',
      lineHeight: '1.25rem',
      textTransform: 'capitalize',
      color: 'var(--color-white)',
      boxShadow: 'none',
      width: '100%',
      boxSizing: 'border-box',
      gap: '0',
      '&:hover': {
        backgroundColor: 'var(--color-brand-hover)',
        borderColor: 'var(--color-brand-hover)',
      },
      '&:focus': {
        boxShadow: 'none',
      },
      '&:disabled, &[data-disabled]': {
        backgroundColor: 'rgba(255, 255, 255, 0.15)',
        borderColor: 'transparent',
        color: 'rgba(255, 255, 255, 0.4)',
      },
    },
    formButtonPrimaryIcon: {
      display: 'none',
    },
    buttonArrowIcon: {
      display: 'none',
    },
    footer: {
      background: 'transparent',
      marginTop: '0.25rem',
      width: '100%',
      overflow: 'visible',
    },
    footerAction: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.25rem',
      width: '100%',
      textAlign: 'center',
    },
    footerActionText: {
      fontFamily: 'Poppins, ui-sans-serif, system-ui, sans-serif',
      fontWeight: '600',
      fontSize: '1rem',
      lineHeight: '1.25rem',
      color: 'var(--color-text)',
      textAlign: 'center',
    },
    footerActionLink: {
      fontFamily: 'Poppins, ui-sans-serif, system-ui, sans-serif',
      fontWeight: '600',
      fontSize: '1rem',
      lineHeight: '1.25rem',
      color: 'var(--color-text)',
      textDecoration: 'underline',
      textUnderlineOffset: '2px',
      textAlign: 'center',
      '&:hover': {
        color: 'var(--color-text)',
      },
    },
    identityPreviewEditButton: {
      color: 'var(--color-text)',
    },
    formFieldErrorText: {
      color: 'var(--error-200)',
      fontSize: '0.75rem',
    },
    alertText: {
      color: 'var(--error-200)',
    },
    dividerLine: {
      backgroundColor: 'var(--color-border)',
    },
    dividerText: {
      color: 'var(--color-text-muted)',
    },
    socialButtonsBlockButton: {
      backgroundColor: 'var(--color-surface)',
      border: '1px solid var(--color-border)',
      borderRadius: '0.5rem',
      color: 'var(--color-text)',
      width: '100%',
      boxSizing: 'border-box',
    },
  },
};
